import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Stack,
} from '@mui/material';
import { DebouncedSearchInput } from '@marageti/z4-lib';

interface FilterField {
  name: string;
  label: string;
  type: 'text' | 'select' | 'date' | 'checkbox';
  options?: { label: string; value: string | number }[];
  value: unknown;
  onChange: (value: unknown) => void;
}

interface AuditFiltersProps {
  fields: FilterField[];
  error: Error | null;
  total: string;
}

const AuditFilters: React.FC<AuditFiltersProps> = ({
  fields,
  error,
  total,
}) => (
  <Stack>
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 1,
        rowGap: 1,
        alignItems: 'flex-end',
      }}
    >
      {fields.map((field) => {
        if (field.type === 'checkbox') {
          return (
            <FormControlLabel
              key={field.name}
              control={(
                <Checkbox
                  checked={Boolean(field.value)}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
            )}
              label={field.label}
            />
          );
        }

        if (field.type === 'select' && field.options) {
          return (
            <FormControl
              key={field.name}
              sx={{
                minWidth: 150,
                marginTop: '20px',
                marginBottom: '10px',
              }}
              size="small"
            >
              <InputLabel>{field.label}</InputLabel>
              <Select
                value={field.value || ''}
                label={field.label}
                onChange={(e) => field.onChange(e.target.value)}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {field.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }

        if (field.type === 'text') {
          return (
            <DebouncedSearchInput
              key={field.name}
              label={field.label}
              name={field.name}
              defaultValue={String(field.value ?? '')}
              onChange={(value: string) => field.onChange(value)}
              size="small"
              sx={{ minWidth: 100, maxWidth: 150 }}
            />
          );
        }

        return (
          <TextField
            key={field.name}
            label={field.label}
            type={field.type}
            value={field.value || ''}
            onChange={(e) => field.onChange(e.target.value)}
            size="small"
            sx={{ minWidth: 100, maxWidth: 150 }}
            InputLabelProps={{ shrink: true }}
          />
        );
      })}
    </Box>
    {error && (
    <Typography color="error" sx={{ mb: 2 }}>
      Error:
      {' '}
      {error?.message || 'An error occurred while retrieving versions'}
    </Typography>
    )}

    <Typography variant="caption" component="p" sx={{ mt: 4 }}>
      {total}
    </Typography>
  </Stack>
);

export default AuditFilters;
